import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

export interface CorrectShiftInput {
  venueId: string;
  shiftId: string;
  openedAt?: string;
  closedAt?: string;
}

export interface Shift {
  id: string;
  employeeId: string;
  employeeName: string;
  venueId: string;
  venueName: string;
  venueTimezone: string;
  openedAt: string;
  closedAt: string | null;
  openedByUserId: string;
  openedByUserLabel: string | null;
  closedByUserId: string | null;
  closedByUserLabel: string | null;
  open: boolean;
}

export interface ShiftListResponse {
  items: Shift[];
}

export interface ListShiftsInput {
  venueId: string;
  state?: 'open' | 'closed';
  from?: string;
  to?: string;
}

const shiftsKey = ['shifts'] as const;

const listShifts = ({ venueId, state, from, to }: ListShiftsInput): Promise<ShiftListResponse> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/shifts', {
      params: { path: { venueId }, query: { state, from, to } },
    }),
  ) as Promise<ShiftListResponse>;

export const openShift = (venueId: string, employeeId: string): Promise<Shift> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/shifts', {
      params: { path: { venueId } },
      body: { employeeId },
    }),
  ) as unknown as Promise<Shift>;

export const closeShift = (venueId: string, shiftId: string): Promise<Shift> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/shifts/{shiftId}/close', {
      params: { path: { venueId, shiftId } },
    }),
  ) as unknown as Promise<Shift>;

export const selfShift = (venueId: string): Promise<Shift | null> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/shifts/me', {
      params: { path: { venueId } },
    }),
    // The endpoint answers 204 when the caller is off-shift here; `unwrap` maps that to
    // undefined, which we normalise to null so the widget can branch on "no shift".
  ).then((shift) => (shift as Shift | undefined) ?? null);

const openSelfShift = (venueId: string, closeExisting: boolean): Promise<Shift> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/shifts/me/open', {
      params: { path: { venueId } },
      body: { closeExisting },
    }),
  ) as unknown as Promise<Shift>;

const closeSelfShift = (venueId: string): Promise<Shift> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/shifts/me/close', {
      params: { path: { venueId } },
    }),
  ) as unknown as Promise<Shift>;

const correctShift = ({
  venueId,
  shiftId,
  openedAt,
  closedAt,
}: CorrectShiftInput): Promise<Shift> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/shifts/{shiftId}', {
      params: { path: { venueId, shiftId } },
      body: { openedAt, closedAt },
    }),
  ) as unknown as Promise<Shift>;

export function useShifts(input: ListShiftsInput, enabled: boolean) {
  return useQuery({
    queryKey: [...shiftsKey, input],
    queryFn: () => listShifts(input),
    enabled,
  });
}

export function useSelfShift(venueId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...shiftsKey, 'me', venueId],
    queryFn: () => selfShift(venueId),
    enabled,
  });
}

function useInvalidateShifts() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: shiftsKey });
}

export function useOpenShift() {
  const invalidate = useInvalidateShifts();
  return useMutation({
    mutationFn: ({ venueId, employeeId }: { venueId: string; employeeId: string }) =>
      openShift(venueId, employeeId),
    onSuccess: invalidate,
  });
}

export function useCloseShift() {
  const invalidate = useInvalidateShifts();
  return useMutation({
    mutationFn: ({ venueId, shiftId }: { venueId: string; shiftId: string }) =>
      closeShift(venueId, shiftId),
    onSuccess: invalidate,
  });
}

export function useOpenSelfShift() {
  const invalidate = useInvalidateShifts();
  return useMutation({
    mutationFn: ({ venueId, closeExisting }: { venueId: string; closeExisting: boolean }) =>
      openSelfShift(venueId, closeExisting),
    onSuccess: invalidate,
  });
}

export function useCloseSelfShift() {
  const invalidate = useInvalidateShifts();
  return useMutation({
    mutationFn: ({ venueId }: { venueId: string }) => closeSelfShift(venueId),
    onSuccess: invalidate,
  });
}

export function useCorrectShift() {
  const invalidate = useInvalidateShifts();
  return useMutation({
    mutationFn: (input: CorrectShiftInput) => correctShift(input),
    onSuccess: invalidate,
  });
}
